<template>
  <v-layout
    column
    class="nx-search-results-toolbar"
  >
    <v-flex>
      <v-layout
        :row="$vuetify.breakpoint.smAndUp"
        :column="$vuetify.breakpoint.xsOnly"
        :align-center="$vuetify.breakpoint.smAndUp"
      >
        <v-flex shrink>
          <v-layout
            row
            align-center
          >
            <v-flex
              v-if="bulkSelection || hasGlobalActions"
              class="actions-container mr-2"
            >
              <v-checkbox
                v-if="bulkSelection"
                v-show="!loading"
                hide-details
                color="secondary"
                class="ma-0 pa-0 pl-2"
                :indeterminate="(!!numSelected && numSelected < numResults) && !checkFullSearch"
                :input-value="(!!numSelected && numResults === numSelected) || checkFullSearch"
                :ripple="false"
                @change="$emit($event ? 'input:check-all' : 'input:uncheck-all')"
              />

              <nx-show-more
                v-if="hasActions"
                v-show="!loading"
                color="dark"
              >
                <v-list>
                  <nx-bulk-action
                    v-for="(action, key) in actions"
                    :key="key"
                    :action="action"
                    @input="$emit('click:action', $event)"
                  />
                </v-list>
              </nx-show-more>

              <nx-show-more
                v-if="hasGlobalActions"
                v-show="!loading"
                color="dark"
              >
                <v-list>
                  <nx-bulk-action
                    v-for="(action, key) in globalActions"
                    :key="key"
                    :action="action"
                    @input="$emit('click:action', $event)"
                  />
                </v-list>
              </nx-show-more>
            </v-flex>

            <v-btn
              v-if="!loading"
              class="ma-0 pa-0"
              icon
              small
              @click="$emit('click:refresh')"
            >
              <v-icon color="black">
                refresh
              </v-icon>
            </v-btn>

            <span
              v-if="!loading"
              class="num-results ml-1"
              v-html="$tc('message.searcher.results', totalItems, { count: humanReadableTotalItems } )"
            />
          </v-layout>
        </v-flex>

        <v-flex>
          <v-layout
            row
            :justify-end="$vuetify.breakpoint.smAndUp"
            :justify-space-between="$vuetify.breakpoint.xsOnly"
          >
            <v-flex
              shrink
              class="mr-2"
            >
              <nx-searcher-sort
                :options="sortFilters"
                :value="sort"
                @input="$emit('input:sort-filters', $event)"
              />
            </v-flex>
            <v-flex
              shrink
              class="mr-2"
            >
              <nx-searcher-limit-selector
                :value="searchLimit"
                :options="searchLimitOptions"
                @input="$listeners['input:search-limit']"
              />
            </v-flex>
            <v-flex shrink>
              <nx-view-selector
                :views="views"
                :value="view"
                @input="$emit('input:view', $event)"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex
      v-if="bulkSelection && (checkFullSearch || (numResults === numSelected && numResults > 0))"
      pa-1
    >
      <v-layout
        align-center
        class="selected-results text-xs-center"
      >
        <v-flex class="text-lg-center">
          <template v-if="!checkFullSearch">
            <span
              class="text-align-middle"
              v-html="$t('message.searcher.bulk.description', { numResults })"
            />
            <v-btn
              color="black"
              class="selected-results-button px-1 mx-0 my-0 text-none text-underline"
              flat
              @click="$emit('input:check-full-search')"
            >
              <span v-html="$t('message.searcher.bulk.selectAll', { total: totalItems })" />
            </v-btn>
          </template>
          <template v-else>
            <span
              class="text-align-middle"
              v-html="$t('message.searcher.bulk.allSelected', { total: totalItems })"
            />
            <v-btn
              color="black"
              class="selected-results-button px-1 mx-0 my-0 text-none text-underline"
              flat
              @click="$emit('input:cancel-full-search')"
              v-text="$t('message.searcher.bulk.cancel')"
            />
          </template>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex v-else-if="bulkSelection && numSelected > 0" pa-1>
      <v-layout align-center class="selected-results text-xs-center">
        <v-flex class="text-lg-center">
          <span class="text-align-middle"
                v-html="$tc('message.searcher.bulk.numSelected', numSelected, { numSelected })"></span>
          <v-btn color="black" class="selected-results-button px-1 mx-0 my-0 text-none text-underline"
                 flat @click="$emit('input:cancel-full-search')"
                 v-text="$t('message.searcher.bulk.cancel')">
          </v-btn>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import NxViewSelector from '@/components/searcher/ViewSelector';
import NxShowMore from '@/components/menu/ShowMore';
import NxSearcherSort from '@/modules/searcher/sort/Sort';
import NxBulkAction from '@/components/bulk/Action';
import NxSearcherLimitSelector from '@/components/searcher/LimitSelector.vue';

export default {
  components: {
    NxSearcherLimitSelector,
    NxShowMore,
    NxSearcherSort,
    NxViewSelector,
    NxBulkAction,
  },
  props: {
    actions: {
      type: Array,
      default: () => ([]),
    },
    bulkSelection: {
      type: Boolean,
      default: false,
    },
    checkFullSearch: {
      type: Boolean,
      default: false,
    },
    globalActions: {
      type: Array,
      default: () => ([]),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    numResults: {
      type: Number,
      default: 0,
    },
    numSelected: {
      type: Number,
      default: 0,
    },
    searchLimit: {
      type: Number,
      required: true,
    },
    searchLimitOptions: {
      type: Array,
      default: () => ([]),
    },
    sort: {
      type: Object,
      default: () => ({}),
    },
    sortFilters: {
      type: Array,
      default: () => ([]),
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    unfilteredActions: {
      type: Array,
      default: () => ([]),
    },
    view: {
      type: Object,
      default: () => ({}),
    },
    views: {
      type: Array,
      default: () => ([]),
    },
  },
  computed: {
    hasActions() {
      if (!this.hasSelectedItems) {
        return false;
      }
      return this.actions.length > 0;
    },
    hasGlobalActions() {
      if (this.hasSelectedItems) {
        return false;
      }
      return this.globalActions.length > 0;
    },
    hasSelectedItems() {
      return this.numSelected > 0;
    },
    humanReadableTotalItems() {
      return new Intl.NumberFormat().format(this.totalItems);
    },
  },
};
</script>

<style lang="stylus" scoped>
.nx-search-results-toolbar {
  .num-results {
    color: #27272e;
    font-weight: 500;
    white-space: nowrap;
  }

  .selected-results {
    padding: 10px;
    min-height: 15px;
    color: #000;
    background-color: #fcde83;
    line-height: 18px;

    &-button {
      height: 15px;
    }
  }

  .actions-container {
    display: flex;
    align-items: center;
    min-width: 60px;
  }
}
</style>
